import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/auth/AuthContext';
import { isSupabaseConfigured } from '@/lib/supabase';
import { type License, type Order } from '@/domain/types';
import { listCurrentUserOrders, orders as mockOrders } from '@/services/orders';
import {
  listCurrentUserLicenses,
  licenses as mockLicenses,
} from '@/services/licenses';
import { useSiteSettings } from '@/settings/useSiteSettings';

export default function DashboardOverview() {
  const navigate = useNavigate();
  const { settings } = useSiteSettings();
  const { user } = useAuth();
  const [userOrders, setUserOrders] = useState<Order[]>([]);
  const [userLicenses, setUserLicenses] = useState<License[]>([]);

  useEffect(() => {
    let isMounted = true;

    async function loadDashboardData() {
      if (!user) {
        if (isMounted) {
          setUserOrders([]);
          setUserLicenses([]);
        }
        return;
      }

      if (!isSupabaseConfigured) {
        if (isMounted) {
          setUserOrders(
            mockOrders.filter(
              (o) => o.userId === user.id || o.userId === user.email,
            ),
          );
          setUserLicenses(
            mockLicenses.filter(
              (l) => l.userId === user.id || l.userId === user.email,
            ),
          );
        }
        return;
      }

      try {
        const [ordersData, licensesData] = await Promise.all([
          listCurrentUserOrders(),
          listCurrentUserLicenses(),
        ]);

        if (isMounted) {
          setUserOrders(ordersData);
          setUserLicenses(licensesData);
        }
      } catch {
        if (isMounted) {
          setUserOrders([]);
          setUserLicenses([]);
        }
      }
    }

    loadDashboardData();
    return () => {
      isMounted = false;
    };
  }, [user]);

  const stats = useMemo(
    () => [
      {
        label: 'Total Orders',
        value: userOrders.length,
        icon: 'ri-shopping-bag-3-line',
        color: 'bg-brand-50 text-brand-600',
      },
      {
        label: 'Active Licenses',
        value: userLicenses.filter((l) => l.status === 'active').length,
        icon: 'ri-key-2-line',
        color: 'bg-green-50 text-green-600',
      },
      {
        label: 'Expired Licenses',
        value: userLicenses.filter((l) => l.status === 'expired').length,
        icon: 'ri-time-line',
        color: 'bg-amber-50 text-amber-600',
      },
      {
        label: 'Total Spent',
        value: `$${userOrders.filter((o) => o.status === 'paid').reduce((s, o) => s + o.amount, 0)}`,
        icon: 'ri-wallet-3-line',
        color: 'bg-sky-50 text-sky-600',
      },
    ],
    [userOrders, userLicenses],
  );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl md:text-2xl font-bold text-surface-900">
          Overview
        </h1>
        <p className="text-sm text-surface-500 mt-1">
          Welcome back. Here is what is happening with your account.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
        {stats.map((s) => (
          <div
            key={s.label}
            className="bg-white rounded-xl border border-surface-200 p-5">
            <div
              className={`w-9 h-9 rounded-lg ${s.color} flex items-center justify-center mb-3`}>
              <i className={`${s.icon} text-lg`} />
            </div>
            <p className="text-2xl font-bold text-surface-900">{s.value}</p>
            <p className="text-xs text-surface-500 mt-0.5">{s.label}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 md:gap-6">
        {/* Recent Orders */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-surface-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-surface-200 flex items-center justify-between">
            <h2 className="font-semibold text-surface-900 text-sm">
              Recent Orders
            </h2>
            <button
              onClick={() => navigate('/dashboard/orders')}
              className="text-xs font-medium text-brand-600 hover:text-brand-700">
              View All
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-surface-100">
                  <th className="text-left px-5 py-3 font-medium text-surface-500 text-xs">
                    Order
                  </th>
                  <th className="text-left px-5 py-3 font-medium text-surface-500 text-xs">
                    Product
                  </th>
                  <th className="text-left px-5 py-3 font-medium text-surface-500 text-xs">
                    Amount
                  </th>
                  <th className="text-left px-5 py-3 font-medium text-surface-500 text-xs">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody>
                {userOrders.slice(0, 4).map((order) => (
                  <tr
                    key={order.id}
                    className="border-b border-surface-50 hover:bg-surface-50/50">
                    <td className="px-5 py-3 text-surface-700 font-medium">
                      {order.orderRef}
                    </td>
                    <td className="px-5 py-3 text-surface-600">
                      {order.productName}
                    </td>
                    <td className="px-5 py-3 text-surface-700 font-medium">
                      ${order.amount}
                    </td>
                    <td className="px-5 py-3">
                      <span
                        className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                          order.status === 'paid'
                            ? 'bg-green-50 text-green-700'
                            : order.status === 'pending'
                              ? 'bg-amber-50 text-amber-700'
                              : 'bg-red-50 text-red-700'
                        }`}>
                        {order.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-xl border border-surface-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-surface-200">
            <h2 className="font-semibold text-surface-900 text-sm">
              Quick Actions
            </h2>
          </div>
          <div className="p-3 space-y-1">
            {settings.dashboard.quickActions
              .filter((item) => item.enabled)
              .map((item) => (
                <button
                  key={item.label}
                  onClick={() => navigate(item.href)}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-surface-700 hover:bg-surface-50 transition-colors">
                  <i className={`${item.icon} text-surface-400`} />
                  {item.label}
                  <i className="ri-arrow-right-s-line ml-auto text-surface-400" />
                </button>
              ))}
          </div>
        </div>
      </div>

      {/* Active Licenses Preview */}
      <div className="bg-white rounded-xl border border-surface-200 overflow-hidden">
        <div className="px-5 py-4 border-b border-surface-200 flex items-center justify-between">
          <h2 className="font-semibold text-surface-900 text-sm">
            Active Licenses
          </h2>
          <button
            onClick={() => navigate('/dashboard/licenses')}
            className="text-xs font-medium text-brand-600 hover:text-brand-700">
            View All
          </button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 p-5">
          {userLicenses
            .filter((l) => l.status === 'active')
            .map((license) => (
              <div
                key={license.id}
                className="p-4 rounded-xl border border-surface-200 bg-surface-50/50">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-semibold text-surface-900 text-sm">
                    {license.productName}
                  </h4>
                  <span className="px-2 py-0.5 rounded-full bg-green-50 text-green-700 text-xs font-medium">
                    {license.status}
                  </span>
                </div>
                <p className="text-xs text-surface-500 font-mono mb-2">
                  ****-****-****-{license.licenseKeyLast4}
                </p>
                <div className="flex items-center justify-between text-xs text-surface-500">
                  <span>
                    {license.activatedSites}/{license.siteLimit} sites used
                  </span>
                  <button
                    onClick={() =>
                      navigate(`/dashboard/licenses/${license.id}`)
                    }
                    className="text-brand-600 hover:text-brand-700 font-medium">
                    Manage
                  </button>
                </div>
                <div className="mt-2 h-1.5 bg-surface-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-brand-500 rounded-full"
                    style={{
                      width: `${(license.activatedSites / license.siteLimit) * 100}%`,
                    }}
                  />
                </div>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}
